import { NextStepProvider, NextStepReact } from 'nextstepjs';
import { ErrorBoundary } from 'react-error-boundary';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router';

import { Auth0Provider } from '@auth0/auth0-react';
import { datadogLogs } from '@datadog/browser-logs';
import { CssBaseline } from '@mui/material';
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import { BloomTheme } from '@sage/ui-components';
import '@sage/ui-components/fonts/bloom.css';
import '@sage/ui-components/fonts/sage.css';

import AnalyticsContextProvider from './AnalyticsContext';
import App from './App';
import CheckCookiesEnabled from './CheckCookiesEnabled';
import DrawerManager from './components/drawers/DrawerManager';
import CaptureDeviceInfo from './components/fragments/CaptureDeviceInfo';
import TrainingToolTip from './components/training/TrainingToolTip';
import { authorizationParams } from './constants/Auth0';
import CareAssignmentsContextProvider from './contexts/CareAssignmentsContext';
import NotificationsContextProvider from './contexts/NotificationsContext';
import './datadog';
import { AlertTours } from './hooks/useAlertTraining';
import ErrorPage from './pages/ErrorPage';
import { store } from './store';
import { useReactRouterAdapter } from 'nextstepjs/adapters/react-router';
import { createRoot } from 'react-dom/client';
import { NativeAuthAvailabilityProvider } from 'src/auth/SageAuthContext';
import NativeListeners from 'src/components/fragments/NativeListeners';
import MicPermissionContextProvider from 'src/contexts/MicPermissionContext';
import { initMixpanel } from 'src/mixpanel';

import './index.css';

initMixpanel();

const container = document.getElementById('root');
const root = createRoot(container!);

// Make sure errors are picked up by Datadog
const logError = (error: any, info: any) => {
    datadogLogs.logger.error('Error caught by error boundary', { info }, error);
};

root.render(
    <BrowserRouter>
        <Provider store={store}>
            <CssBaseline>
                <StyledEngineProvider injectFirst>
                    <ThemeProvider theme={BloomTheme}>
                        <ErrorBoundary fallbackRender={ErrorPage} onError={logError}>
                            <CheckCookiesEnabled>
                                <NativeAuthAvailabilityProvider>
                                    <Auth0Provider
                                        domain={import.meta.env.VITE_AUTH0_DOMAIN}
                                        clientId={import.meta.env.VITE_AUTH0_CLIENT_ID}
                                        authorizationParams={authorizationParams}
                                        cacheLocation={import.meta.env.VITE_AUTH0_STORAGE_LOCATION}
                                        useRefreshTokens
                                        // See: https://community.auth0.com/t/auth0-spa-2-x-returning-missing-refresh-token/98999/18
                                        useRefreshTokensFallback
                                    >
                                        <AnalyticsContextProvider>
                                            <NotificationsContextProvider>
                                                <MicPermissionContextProvider>
                                                    <CareAssignmentsContextProvider>
                                                        <NextStepProvider>
                                                            <NextStepReact
                                                                steps={AlertTours}
                                                                navigationAdapter={
                                                                    useReactRouterAdapter
                                                                }
                                                                cardComponent={TrainingToolTip}
                                                                cardTransition={{
                                                                    ease: 'easeOut',
                                                                    duration: 0.2,
                                                                }}
                                                                scrollToTop={false}
                                                                disableConsoleLogs={
                                                                    import.meta.env.PROD
                                                                }
                                                            >
                                                                <DrawerManager>
                                                                    <CaptureDeviceInfo />
                                                                    <NativeListeners />
                                                                    <App />
                                                                </DrawerManager>
                                                            </NextStepReact>
                                                        </NextStepProvider>
                                                    </CareAssignmentsContextProvider>
                                                </MicPermissionContextProvider>
                                            </NotificationsContextProvider>
                                        </AnalyticsContextProvider>
                                    </Auth0Provider>
                                </NativeAuthAvailabilityProvider>
                            </CheckCookiesEnabled>
                        </ErrorBoundary>
                    </ThemeProvider>
                </StyledEngineProvider>
            </CssBaseline>
        </Provider>
    </BrowserRouter>
);
